import { GridColumn } from '@progress/kendo-react-grid'
import { useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import { HStack } from '~/components/elements/HStack'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { StyledGrid } from '~/components/elements/StyledGrid'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useLanguageTextWidth } from '~/hooks/useLanguageTextWidth'
import { useSurveyQueryCustomerAttributeGroups } from '~/hooks/useSurveyQuery'

import { dialogInfoAtom } from '../atoms'
import { useSurveyId } from '../hooks/useSurveyId'
import { pathKey } from '../utils'

import { CommandsCell } from './CommandsCell'
import { Dialogs } from './Dialogs'
import { NameCell } from './NameCell'

export const CustomerAttributeSettings = () => {
  const surveyId = useSurveyId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('advancedAnalysis'),
      createBreadcrumb('customerAttributeAnalysis', { surveyId }),
      createBreadcrumb(pathKey, { surveyId }),
    ],
    [createBreadcrumb, surveyId],
  )

  const customerAttributeGroups = useSurveyQueryCustomerAttributeGroups()

  const setDialogInfo = useSetAtom(dialogInfoAtom)
  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    setDialogInfo({ dialogType: 'create' })
  }, [setDialogInfo])
  const handleSortButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    setDialogInfo({ dialogType: 'sort' })
  }, [setDialogInfo])

  const deleteButtonWidth = useLanguageTextWidth({ 'ja-JP': 82, 'en-US': 116 })
  // padding + button + space + button + padding
  const commandsCellWidth = 12 + 132 + 4 + deleteButtonWidth + 12

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <VStack align="start" spacing={4} verticalAppearance="stretch">
        <Typography variant="heading1">
          {t('customized_segments_settings')}
        </Typography>

        <VStack spacing={2} verticalAppearance="scroll">
          <Typography variant="heading2">
            {t('customized_segments_group')}
          </Typography>

          <StyledGrid data={customerAttributeGroups}>
            <GridColumn
              cell={NameCell}
              field="name"
              title={t('customized_segments_group_name')}
            />

            <GridColumn cell={CommandsCell} width={commandsCellWidth} />
          </StyledGrid>
        </VStack>

        <HStack spacing={2}>
          <StyledButton
            buttonStyle="success"
            icon="add"
            onClick={handleAddButtonClick}
          >
            {t('add_group')}
          </StyledButton>

          <StyledButton
            buttonStyle="success"
            icon="arrows-swap"
            onClick={handleSortButtonClick}
          >
            {t('sort')}
          </StyledButton>
        </HStack>

        <Dialogs />
      </VStack>
    </PageContainer>
  )
}
